import React, { useState } from 'react';
import './ResetPassword.css';
import logoImg from '../../assets/logo.png';

const ResetPassword = ({ onSwitchView }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    code: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Secure card expand/retract state (retracted on mobile by default to focus on form, expanded on desktop)
  const [isSecureCardExpanded, setIsSecureCardExpanded] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth > 992;
    }
    return false;
  });

  const toggleSecureCard = () => {
    setIsSecureCardExpanded(prev => !prev);
  };

  const passwordCriteria = {
    length: formData.newPassword.length >= 8 && formData.newPassword.length <= 20,
    uppercase: /[A-Z]/.test(formData.newPassword),
    specialChar: /[!@#$%^&*(),.?":{}|<>\-_+=[\]\\/'`]/.test(formData.newPassword)
  };

  const isPasswordValid = Object.values(passwordCriteria).every(Boolean);
  const passwordStrength = Object.values(passwordCriteria).filter(Boolean).length;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: formData.email }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMsg(data.message || 'Reset code sent to your email.');
        setStep(2);
      } else {
        setErrorMsg(data.message || 'Failed to send reset code.');
      }
    } catch (error) {
      console.error('Reset request error:', error);
      setErrorMsg('Failed to connect to the server.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    if (!isPasswordValid) {
      setErrorMsg('Please ensure your password meets all requirements.');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setErrorMsg('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          code: formData.code,
          newPassword: formData.newPassword
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMsg(data.message || 'Password has been reset successfully.');
        
        // Switch back to login after 2 seconds
        setTimeout(() => {
          if(onSwitchView) onSwitchView('login');
        }, 2000);
      } else {
        setErrorMsg(data.message || 'Failed to reset password.');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setErrorMsg('Failed to reset password. Server connection error.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="split-layout-container">
      {/* Left Panel */}
      <div className="left-panel">
        <div className="left-panel-top">
          <img src={logoImg} alt="Brand Logo" className="brand-logo" />
        </div>
        
        <div className="left-panel-middle">
        </div>

        <div className="left-panel-bottom">
          <div className={`secure-registration-card ${isSecureCardExpanded ? 'expanded' : 'retracted'}`}>
            <div 
              className="secure-card-header" 
              onClick={toggleSecureCard}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleSecureCard();
                }
              }}
              role="button"
              tabIndex={0}
              aria-expanded={isSecureCardExpanded}
              aria-label={isSecureCardExpanded ? "Retract security information" : "Expand security information"}
            >
              <div className="secure-header-left">
                <div className="secure-icon-badge">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shield-icon-mini">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                    <path d="M12 8v4"></path>
                    <path d="M12 16h.01"></path>
                  </svg>
                </div>
                <div className="secure-header-text">
                  <h3>Account Recovery</h3>
                  <span className="secure-status-tag">256-bit SSL Encrypted</span>
                </div>
              </div>
              <div className="secure-header-action">
                <span className="secure-toggle-hint">{isSecureCardExpanded ? 'Hide' : 'Details'}</span>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  className={`secure-card-chevron ${isSecureCardExpanded ? 'open' : ''}`}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
            </div>

            <div className="secure-card-body">
              <div className="secure-icon-wrapper desktop-only-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shield-icon">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                  <path d="M12 8v4"></path>
                  <path d="M12 16h.01"></path>
                </svg>
              </div>
              <p className="secure-card-description">
                We'll help you get back into your account securely and as quickly as possible.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="right-panel" style={{ alignItems: 'center' }}>
        <div className="form-container" style={{ maxWidth: '420px', margin: '0 auto', width: '100%' }}>
          
          <div className="reset-header-wrapper">
            <div className="reset-title-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
            </div>
            <h2>{step === 1 ? 'Forgot Password' : 'Reset Password'}</h2>
            <p>{step === 1 ? 'Enter your email address to receive a reset code' : 'Create a new password for your account'}</p>
          </div>

          {errorMsg && (
            <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
              {errorMsg}
            </div>
          )}
          
          {successMsg && (
            <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
              {successMsg}
            </div>
          )}

          {step === 1 ? (
            <form onSubmit={handleRequestReset} className="reset-form">
              <div className="form-group full" style={{ gap: '0.5rem' }}>
                <label htmlFor="email" style={{ padding: 0 }}>Email Address</label>
                <div className="input-with-icon">
                  <svg className="input-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    value={formData.email} 
                    onChange={handleChange} 
                    required 
                    placeholder="Enter your email" 
                  />
                </div>
              </div>

              <button type="submit" className="reset-submit-btn" disabled={isSubmitting}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
                {isSubmitting ? 'Sending...' : 'Send Reset Code'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="reset-form">
              <div className="form-group full" style={{ gap: '0.5rem' }}>
                <label htmlFor="code" style={{ padding: 0 }}>Reset Code</label>
                <div className="input-with-icon">
                  <svg className="input-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                  <input 
                    type="text" 
                    id="code" 
                    name="code" 
                    value={formData.code} 
                    onChange={handleChange} 
                    required 
                    placeholder="Enter 6-digit code" 
                    data-hj-suppress
                  />
                </div>
              </div>

              <div className="form-group full" style={{ gap: '0.5rem' }}>
                <label htmlFor="newPassword" style={{ padding: 0 }}>New Password</label>
                <div className="password-input-wrapper" style={{ position: 'relative' }}>
                  <div className="input-with-icon" style={{ width: '100%' }}>
                    <svg className="input-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                    </svg>
                    <input 
                      type={showPassword ? "text" : "password"} 
                      id="newPassword" 
                      name="newPassword" 
                      value={formData.newPassword} 
                      onChange={handleChange} 
                      required 
                      placeholder="Enter new password" 
                      style={{ paddingRight: '2.5rem' }}
                      data-hj-suppress
                    />
                  </div>
                  <div className="eye-icon" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#94a3b8' }}>
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    )}
                  </div>
                </div>
                {/* Password Strength Checker */}
                {formData.newPassword.length > 0 && (
                  <div className="password-strength-checker">
                    <div className="strength-bar-container">
                      <div
                        className={`strength-bar-fill strength-${passwordStrength}`}
                        style={{ width: `${(passwordStrength / 3) * 100}%` }}
                      ></div>
                    </div>
                    <ul className="strength-criteria-list">
                      <li className={passwordCriteria.length ? 'met' : 'unmet'}>
                        <span className="bullet"></span> At least 8-20 characters
                      </li>
                      <li className={passwordCriteria.uppercase ? 'met' : 'unmet'}>
                        <span className="bullet"></span> Contains 1 uppercase letter
                      </li>
                      <li className={passwordCriteria.specialChar ? 'met' : 'unmet'}>
                        <span className="bullet"></span> Contains 1 special character
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              <div className="form-group full" style={{ gap: '0.5rem' }}>
                <label htmlFor="confirmPassword" style={{ padding: 0 }}>Confirm Password</label>
                <div className="password-input-wrapper" style={{ position: 'relative' }}>
                  <div className="input-with-icon" style={{ width: '100%' }}>
                    <svg className="input-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                    </svg>
                    <input 
                      type={showConfirmPassword ? "text" : "password"} 
                      id="confirmPassword" 
                      name="confirmPassword" 
                      value={formData.confirmPassword} 
                      onChange={handleChange} 
                      required 
                      placeholder="Confirm new password" 
                      style={{ paddingRight: '2.5rem' }}
                      data-hj-suppress
                    />
                  </div>
                  <div className="eye-icon" onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#94a3b8' }}>
                    {showConfirmPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    )}
                  </div>
                </div>
              </div>

              <button type="submit" className="reset-submit-btn" disabled={isSubmitting}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                {isSubmitting ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          )}
          
          <div className="back-to-login">
            <a href="#login" className="login-link" onClick={(e) => { e.preventDefault(); if(onSwitchView) onSwitchView('login'); }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.5rem' }}>
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              Back to Login
            </a>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
